import { Wifi, Tv, Snowflake, Bath, CookingPot, Sofa, BedDouble, BedSingle } from "lucide-react";

export const AMENITY_DEFS = {
    wifi: { icon: Wifi, label: "Wifi", labelEn: "Wifi" },
    tv: { icon: Tv, label: "TV inteligente", labelEn: "Smart TV" },
    ac: { icon: Snowflake, label: "Aire acondicionado", labelEn: "Air conditioning" },
    bath: { icon: Bath, label: "Baño completo privado", labelEn: "Full private bathroom" },
    kitchen: { icon: CookingPot, label: "Cocineta equipada", labelEn: "Equipped kitchenette" },
    living: { icon: Sofa, label: "Sala de estar", labelEn: "Living room" },
};

export const resolveAmenities = (room) =>
    (room.amenityKeys || []).map((key) => ({ key, ...AMENITY_DEFS[key] })).filter((a) => a.icon);

export const getBedIcon = (room) => (/individual|twin/i.test(room.beds || "") ? BedSingle : BedDouble);

export const rooms = [
    {
        slug: "habitacion-sencilla",
        photo: "/fotos/habitacion-sencilla-05.jpg",
        name: "Habitación Sencilla",
        tagline: "Refugio íntimo",
        capacity: "2 personas (máx. 3)",
        guests: 2,
        beds: "1 cama matrimonial",
        amenityKeys: ["wifi", "tv", "ac", "bath"],
        description:
            "Perfecta para parejas: cama matrimonial, aire acondicionado, wifi y baño completo privado. La opción más práctica para descansar a unos pasos de la playa.",
        en: {
            name: "Single Room",
            tagline: "Intimate retreat",
            capacity: "2 guests (max. 3)",
            beds: "1 double bed",
            description:
                "Perfect for couples: double bed, air conditioning, wifi and a full private bathroom. The most practical option to rest just steps from the beach.",
        },
        cardCaption: "[Foto: Habitación Sencilla — cama matrimonial con textiles boho]",
        gallery: [
            { src: "/fotos/habitacion-sencilla-01.jpg", alt: "Cama matrimonial con cabecera de madera" },
            { src: "/fotos/habitacion-sencilla-02.jpg", alt: "Vista lateral de la cama matrimonial" },
            { src: "/fotos/habitacion-sencilla-03.jpg", alt: "Baño completo con tina" },
            { src: "/fotos/habitacion-sencilla-04.jpg", alt: "Sala con sofá, TV y escritorio" },
        ],
    },
    {
        slug: "habitacion-doble",
        photo: "/fotos/habitacion-doble-01.jpg",
        name: "Habitación Doble",
        tagline: "Para compartir",
        capacity: "4 personas",
        guests: 4,
        beds: "2 camas matrimoniales",
        amenityKeys: ["wifi", "tv", "ac", "bath"],
        description:
            "Dos camas matrimoniales, aire acondicionado y espacio de sobra para cuatro personas. La favorita de las familias que vienen a la playa.",
        en: {
            name: "Double Room",
            tagline: "Made for sharing",
            capacity: "4 guests",
            beds: "2 double beds",
            description:
                "Two double beds, air conditioning and plenty of room for four. The family favorite for beach trips.",
        },
        cardCaption: "[Foto: Habitación Doble — dos camas matrimoniales]",
        gallery: [
            { src: "/fotos/habitacion-doble-02.jpg", alt: "Vista de la habitación hacia la TV y el baño" },
            { src: "/fotos/bano-compartido-01.jpg", alt: "Baño completo con tina y azulejo" },
        ],
    },
    {
        slug: "habitacion-triple",
        photo: "/fotos/habitacion-triple-01.jpg",
        name: "Habitación Triple",
        tagline: "En tribu",
        capacity: "3 personas",
        guests: 3,
        beds: "1 cama matrimonial + 1 individual",
        amenityKeys: ["wifi", "tv", "ac", "bath"],
        description:
            "Una cama matrimonial y una individual en un espacio fresco y amplio. Ideal para tres personas que viajan juntas.",
        en: {
            name: "Triple Room",
            tagline: "With your crew",
            capacity: "3 guests",
            beds: "1 double bed + 1 twin bed",
            description:
                "One double bed and one twin bed in a fresh, spacious room. Ideal for three people traveling together.",
        },
        cardCaption: "[Foto: Habitación Triple — cama matrimonial e individual]",
        gallery: [
            { src: "/fotos/habitacion-triple-02.jpg", alt: "Cama matrimonial e individual, segundo ángulo" },
            { src: "/fotos/habitacion-triple-03.jpg", alt: "Cama, TV y acceso al baño" },
            { src: "/fotos/habitacion-triple-04.jpg", alt: "Cama matrimonial con vista al baño" },
            { src: "/fotos/habitacion-triple-05.jpg", alt: "Camas con muro rosa y verde" },
            { src: "/fotos/habitacion-triple-06.jpg", alt: "Baño con regadera de cancel y lavabo" },
        ],
    },
    {
        slug: "bungalow-sencillo",
        photo: "/fotos/bungalow-sencillo-01.jpg",
        name: "Bungalow Sencillo",
        tagline: "Tu rincón independiente",
        capacity: "2 personas",
        guests: 2,
        beds: "1 cama king size",
        amenityKeys: ["kitchen", "wifi", "tv", "ac", "bath"],
        description:
            "Cama king size y cocineta equipada en un bungalow independiente. Prepara tu café por la mañana y camina a la playa en dos minutos.",
        en: {
            name: "Single Bungalow",
            tagline: "Your own private corner",
            capacity: "2 guests",
            beds: "1 king bed",
            description:
                "King bed and equipped kitchenette in a private bungalow. Make your morning coffee and walk to the beach in two minutes.",
        },
        cardCaption: "[Foto: Bungalow Sencillo — cama king size]",
        gallery: [
            { src: "/fotos/bungalow-sencillo-02.jpg", alt: "Cocineta equipada con tarja y parrilla" },
            { src: "/fotos/bungalow-sencillo-03.jpg", alt: "Baño con azulejo blanco y negro" },
        ],
    },
    {
        slug: "bungalow-doble",
        photo: "/fotos/bungalow-doble-05.jpg",
        name: "Bungalow Doble",
        tagline: "Independencia total",
        capacity: "4 personas",
        guests: 4,
        beds: "2 camas matrimoniales",
        amenityKeys: ["kitchen", "wifi", "tv", "ac", "bath"],
        description:
            "Dos camas matrimoniales, cocineta equipada y comedor. Tu casita independiente en Punta Pérula, perfecta para familias.",
        en: {
            name: "Double Bungalow",
            tagline: "Total independence",
            capacity: "4 guests",
            beds: "2 double beds",
            description:
                "Two double beds, equipped kitchenette and dining area. Your own little house in Punta Pérula, perfect for families.",
        },
        cardCaption: "[Foto: Bungalow Doble — dos camas matrimoniales]",
        gallery: [
            { src: "/fotos/bungalow-doble-02.jpg", alt: "Dos camas matrimoniales" },
            { src: "/fotos/bungalow-doble-03.jpg", alt: "Camas con muro verde y naranja" },
            { src: "/fotos/bungalow-doble-04.jpg", alt: "Comedor y cocineta equipada" },
            { src: "/fotos/bungalow-doble-01.jpg", alt: "Baño completo con azulejo" },
        ],
    },
    {
        slug: "suite-sencilla",
        photo: "/fotos/suite-sencilla-03.jpg",
        name: "Suite Sencilla",
        tagline: "Con tina de hidromasaje",
        capacity: "2 personas",
        guests: 2,
        beds: "1 cama king size",
        amenityKeys: ["living", "wifi", "tv", "ac", "bath"],
        description:
            "Cama king size, sala de estar y tina de hidromasaje dentro de la habitación. Nuestra suite más pedida por las parejas.",
        en: {
            name: "Single Suite",
            tagline: "With a jacuzzi tub",
            capacity: "2 guests",
            beds: "1 king bed",
            description:
                "King bed, living area and an in-room jacuzzi tub. Our most requested suite for couples.",
        },
        cardCaption: "[Foto: Suite Sencilla — cama king y sala de estar]",
        gallery: [
            { src: "/fotos/suite-sencilla-02.jpg", alt: "Tina de hidromasaje en la habitación" },
            { src: "/fotos/suite-sencilla-01.jpg", alt: "Barra comedor y cocineta equipada" },
            { src: "/fotos/suite-sencilla-04.jpg", alt: "Tocador con espejo de marco de madera" },
            { src: "/fotos/suite-sencilla-05.jpg", alt: "Tocador y lavabo" },
        ],
    },
    {
        slug: "suite-doble",
        photo: "/fotos/suite-doble-02.jpg",
        name: "Suite Doble",
        tagline: "Amplitud costera",
        capacity: "4 personas",
        guests: 4,
        beds: "2 camas queen",
        amenityKeys: ["living", "wifi", "tv", "ac", "bath"],
        description:
            "Dos camas queen, sala de estar y baño amplio con regadera y tina. La opción más espaciosa del hotel para cuatro personas.",
        en: {
            name: "Double Suite",
            tagline: "Coastal spaciousness",
            capacity: "4 guests",
            beds: "2 queen beds",
            description:
                "Two queen beds, living area and a large bathroom with shower and tub. The most spacious option in the hotel for four guests.",
        },
        cardCaption: "[Foto: Suite Doble — dos camas queen]",
        gallery: [
            { src: "/fotos/suite-doble-01.jpg", alt: "Dos camas queen junto a la ventana" },
            { src: "/fotos/suite-doble-03.jpg", alt: "Baño amplio con regadera y tina" },
            { src: "/fotos/suite-doble-04.jpg", alt: "Regadera con cancel de vidrio" },
            { src: "/fotos/suite-doble-05.jpg", alt: "Cocineta con barra y taburetes" },
            { src: "/fotos/suite-doble-06.jpg", alt: "Barra comedor con vista a la habitación" },
        ],
    },
];

export const getRoomBySlug = (slug) => rooms.find((r) => r.slug === slug);

export const resolveRoom = (room, lang) =>
    lang === "en" && room.en && room.en.name ? { ...room, ...room.en } : room;
